import { VARIATION_WEIGHT } from "../constants/VariationConstant";
import { articleCode, isPackageProduct, normaliseWeight, productKey } from "./articleCode";
import { piecesPerPackFor } from "./packaging";

/**
 * From an order to the lines a picker actually picks.
 *
 * This is the one expansion of package products for everything that is picked or printed for
 * picking: the wave document, and - from step 4 of the build plan - the packing slip. Before
 * this there were three, and they disagreed: the packing slip multiplied a missing WEIGHT
 * quantity into NaN, merged two different articles that happened to share a name, and crashed
 * the whole day's print on a deleted package component.
 *
 * The rules are the Picqer mapping's (planning/picqer-productmapping.md), which were decided and
 * tested for exactly this purpose:
 *
 *  - package contents come from the PRODUCT document (`productItems[]`); order lines never carry
 *    them;
 *  - a WEIGHT component is `quantity` units (default 1) of `value` grams, a PIECES component is
 *    `value` pieces (default 1), both times the number of packages ordered;
 *  - lines merge on article code, never on name;
 *  - gift cards are skipped;
 *  - anything that cannot be mapped refuses the ORDER with a readable reason. Guessing whether an
 *    unknown product is a package would risk sending the picker for the box instead of what is
 *    in it, and an order that quietly loses a line is worse than one that visibly does not map.
 *
 * On top of the Picqer rules, packages may contain packages (a kerstpakket holding a
 * bbq-pakket), followed to the same depth as the SooCool colli calculation. A package that
 * contains itself, or nests deeper than that, is a catalogue error and refuses the order.
 *
 * Pure: no Firestore, no React.
 */

export const MAX_PACKAGE_DEPTH = 3;

/** An order that expands into more pieces than this has a typo in it, not a shipment. */
export const MAX_PICK_UNITS = 5000;

/** Products from useProducts (both shops) keyed on `${shopSource}|${id}`. Build once per render. */
export const buildProductIndex = (products) => {
  const index = new Map();
  (products || []).forEach((product) => {
    if (!!product && !!product.id && !!product.shopSource) index.set(productKey(product.shopSource, product.id), product);
  });
  return index;
};

/**
 * The products of one shop, looked up by bare product id - what an order (which is always from
 * one shop) and the colli calculation need.
 */
export const catalogueForShop = (productIndex, shopSource) => ({
  get: (productId) => (!productId || !productIndex ? undefined : productIndex.get(productKey(shopSource, productId))),
});

/** A count as the CMS stores it: a number or a numeric string. null when absent, NaN when nonsense. */
const parseCount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  const text = String(value).trim().replace(",", ".");
  if (text === "") return null;
  return /^[+-]?\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
};

/**
 * Expand one order into pick lines.
 *
 * @param {Object} order - an order as useOrders returns it (with `shopSource`)
 * @param {Map} productIndex - from buildProductIndex, both shops
 * @returns {{ok: true, lines: Object[]} | {ok: false, reason: string, problems: string[]}}
 *   lines: `{ articleCode, shopSource, productId, name, weight, quantity, piecesPerPack, pickNote,
 *   sources }`, in the order they were first met. `piecesPerPack` is the product's own for that
 *   weight (null: one per package), never the linked product's: the two shops may sell the same
 *   meat in other units.
 */
export const expandOrderToPickLines = (order, productIndex, { maxDepth = MAX_PACKAGE_DEPTH } = {}) => {
  const shopSource = order?.shopSource;
  if (!shopSource) {
    return { ok: false, reason: "no-shop", problems: ["Bestelling heeft geen shop (VCE/HVO)."] };
  }
  if (!productIndex || productIndex.size === 0) {
    // Never map against an empty catalogue: every package would look like a plain article.
    return { ok: false, reason: "empty-catalogue", problems: ["De productcatalogus is niet geladen."] };
  }

  const problems = [];
  const byCode = new Map();
  let totalUnits = 0;
  const lookup = (productId) => productIndex.get(productKey(shopSource, productId));

  const merge = (productId, product, weight, amount, source, variantLabel = null) => {
    const normalisedWeight = normaliseWeight(weight);
    const code = articleCode(shopSource, productId, normalisedWeight);
    totalUnits += amount;
    const existing = byCode.get(code);
    if (existing) {
      existing.quantity += amount;
      if (!existing.sources.includes(source)) existing.sources.push(source);
      return;
    }
    byCode.set(code, {
      articleCode: code,
      shopSource,
      productId,
      // The product document's name, not the order line's: the line keeps whatever the product
      // was called at checkout, the picker needs what is on the bordje now.
      name: product.name || "",
      weight: normalisedWeight,
      quantity: amount,
      piecesPerPack: piecesPerPackFor(product, normalisedWeight),
      pickNote: product.pickNote || null,
      // What the packing slip prints after the name, as the webshop labelled the variant
      variantLabel: variantLabel || null,
      // Only for sorting a slip while there is no pick route yet (sortLinesByPurchased)
      purchased: product.purchased || 0,
      sources: [source],
    });
  };

  const expandPackage = (pkg, packageId, multiplier, ancestry, depth) => {
    if (depth > maxDepth) {
      problems.push(`Pakket '${pkg.name}' zit dieper dan ${maxDepth} niveaus in andere pakketten.`);
      return;
    }
    pkg.productItems.forEach((item) => {
      if (!item) return;
      if (!item.productId) {
        problems.push(`Pakket '${pkg.name}' heeft een onderdeel zonder productId.`);
        return;
      }
      if (ancestry.includes(item.productId)) {
        problems.push(`Pakket '${pkg.name}' bevat zichzelf (${item.productId}).`);
        return;
      }
      const component = lookup(item.productId);
      if (!component) {
        problems.push(`Pakket '${pkg.name}' verwijst naar onbekend onderdeel ${item.productId}.`);
        return;
      }
      if (component.giftCard) return;

      const byWeight = item.type === VARIATION_WEIGHT;
      const counted = parseCount(byWeight ? item.quantity : item.value);
      const units = counted === null ? 1 : byWeight ? counted : Math.round(counted);
      if (!Number.isInteger(units) || units <= 0) {
        problems.push(`Onderdeel '${component.name}' in pakket '${pkg.name}' komt uit op ${units} stuks.`);
        return;
      }

      if (isPackageProduct(component)) {
        expandPackage(component, item.productId, units * multiplier, [...ancestry, item.productId], depth + 1);
        return;
      }
      merge(item.productId, component, byWeight ? item.value : null, units * multiplier, `package:${packageId}`);
    });
  };

  const lines = Array.isArray(order.products) ? order.products : [];
  if (lines.length === 0) problems.push("Bestelling heeft geen producten.");

  lines.forEach((line) => {
    if (!line || line.giftCard) return;

    const quantity = parseCount(line.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      problems.push(`Regel '${line.name}' heeft aantal ${line.quantity}.`);
      return;
    }

    const product = line.productId ? lookup(line.productId) : null;
    if (!product) {
      problems.push(
        `Product '${line.name}' (id ${line.productId}) staat niet in de productcatalogus, dus is niet te bepalen of het een pakket is.`,
      );
      return;
    }
    if (product.giftCard) return;

    if (isPackageProduct(product)) {
      expandPackage(product, line.productId, quantity, [line.productId], 1);
      return;
    }
    merge(line.productId, product, line.variation?.weight, quantity, "line", line.type);
  });

  if (totalUnits > MAX_PICK_UNITS) {
    problems.push(`Bestelling komt uit op ${totalUnits} stuks, meer dan ${MAX_PICK_UNITS}. Controleer de aantallen.`);
  }
  if (problems.length > 0) return { ok: false, reason: "mapping-failed", problems };
  if (byCode.size === 0) {
    return { ok: false, reason: "no-pickable-lines", problems: ["Bestelling heeft na uitklappen geen te picken artikelen."] };
  }
  return { ok: true, lines: [...byCode.values()] };
};
